// app/product-categories/BrandFilterClient.tsx
'use client';

import Link from 'next/link';
import Image from 'next/image';
import { useState, useEffect } from 'react';

interface Brand {
    id: number;
    name: string;
    slug: string;
    productCount: number;
    brandThumbnail?: string;
}

interface BrandFilterClientProps {
    brands: Brand[];
    categorySlug: string;
    currentBrandFilter: string;
    totalProducts: number;
    isMobile?: boolean;
}

export default function BrandFilterClient({
    brands,
    categorySlug,
    currentBrandFilter,
    totalProducts,
    isMobile = false
}: BrandFilterClientProps) {
    const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false);
    const [isExpanded, setIsExpanded] = useState(false);
    const [expandedBrands, setExpandedBrands] = useState<Brand[]>([]);

    // Определим количество брендов для отображения в свернутом состоянии
    const initialBrandsCount = 21;
    const visibleBrands = brands.slice(0, initialBrandsCount);
    const hasMoreBrands = brands.length > initialBrandsCount;

    useEffect(() => {
        // Устанавливаем расширенные бренды только когда открывается список
        if (isExpanded) {
            setExpandedBrands(brands.slice(initialBrandsCount));
        }
    }, [isExpanded, brands]);

    const toggleMobileFilter = () => {
        setIsMobileFilterOpen(!isMobileFilterOpen);
    };

    const toggleExpand = () => {
        setIsExpanded(!isExpanded);
    };

    const handleLinkClick = () => {
        if (isMobileFilterOpen) {
            setIsMobileFilterOpen(false);
        }
    };

    // Мобильная версия фильтра
    if (isMobile) {
        return (
            <>
                {/* Мобильная кнопка фильтра */}
                <div className="sticky top-0 z-30 bg-white">
                    <button
                        onClick={toggleMobileFilter}
                        className="bg-[#fff] hover:bg-[#fff] w-fit p-0 gap-[5px] text-[#4b5563] rounded-md flex items-center justify-center"
                    >
                        <svg className='w-[20px]' viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><g id="SVGRepo_bgCarrier" strokeWidth="0"></g><g id="SVGRepo_tracerCarrier" strokeLinecap="round" strokeLinejoin="round"></g><g id="SVGRepo_iconCarrier"> <path d="M21 6H19M21 12H16M21 18H16M7 20V13.5612C7 13.3532 7 13.2492 6.97958 13.1497C6.96147 13.0615 6.93151 12.9761 6.89052 12.8958C6.84431 12.8054 6.77934 12.7242 6.64939 12.5617L3.35061 8.43826C3.22066 8.27583 3.15569 8.19461 3.10948 8.10417C3.06849 8.02393 3.03853 7.93852 3.02042 7.85026C3 7.75078 3 7.64677 3 7.43875V5.6C3 5.03995 3 4.75992 3.10899 4.54601C3.20487 4.35785 3.35785 4.20487 3.54601 4.10899C3.75992 4 4.03995 4 4.6 4H13.4C13.9601 4 14.2401 4 14.454 4.10899C14.6422 4.20487 14.7951 4.35785 14.891 4.54601C15 4.75992 15 5.03995 15 5.6V7.43875C15 7.64677 15 7.75078 14.9796 7.85026C14.9615 7.93852 14.9315 8.02393 14.8905 8.10417C14.8443 8.19461 14.7793 8.27583 14.6494 8.43826L11.3506 12.5617C11.2207 12.7242 11.1557 12.8054 11.1095 12.8958C11.0685 12.9761 11.0385 13.0615 11.0204 13.1497C11 13.2492 11 13.3532 11 13.5612V17L7 20Z" stroke="#383c42" strokeWidth="0.9359999999999999" strokeLinecap="round" strokeLinejoin="round"></path> </g></svg>
                        Фильтры
                    </button>
                </div>

                {/* Мобильный фильтр - боковая панель */}
                <div className={`fixed inset-y-0 left-0 z-[100000001] w-[340px] bg-white shadow-lg transform ${isMobileFilterOpen ? 'translate-x-0' : '-translate-x-full'} transition-transform duration-300 ease-in-out`}>
                    <div className="h-full flex flex-col">
                        <div style={{ borderBottom: '1px solid #d3d3d3' }} className="p-4 flex justify-between items-center">
                            <h3 className="font-medium">Бренды</h3>
                            <button
                                onClick={toggleMobileFilter}
                                className="bg-[#fff] hover:bg-[#fff] w-8 h-8 flex items-center justify-center text-gray-500 hover:text-gray-700"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        <div className="flex-1 overflow-y-auto p-4">
                            <div className="grid grid-cols-3 gap-3">
                                {brands.map((brand) => (
                                    <Link
                                        key={brand.id}
                                        href={`/product-tag/${categorySlug}?brand=${brand.slug}&page=1`}
                                        onClick={handleLinkClick}
                                        className={`brand_filter_image flex flex-col items-center justify-center p-3 rounded-lg border ${currentBrandFilter === brand.slug
                                            ? 'border-[#ff7900] bg-orange-50'
                                            : 'border-[#ddd]'
                                            } aspect-square`}
                                    >
                                        {brand.brandThumbnail ? (
                                            <div className="mb-2 flex items-center justify-center h-[50px]">
                                                <Image
                                                    src={brand.brandThumbnail}
                                                    alt={brand.name}
                                                    width={50}
                                                    height={50}
                                                    className="rounded-md object-contain max-h-[50px]"
                                                />
                                            </div>
                                        ) : (
                                            <div className="mb-2 flex items-center justify-center h-[50px] w-[50px] bg-gray-100 rounded-full">
                                                <span className="text-xl font-semibold text-gray-400">{brand.name.charAt(0)}</span>
                                            </div>
                                        )}
                                        <span className="text-xs text-center w-full">{brand.name}</span>
                                        <span className="hidden">({brand.productCount})</span>
                                    </Link>
                                ))}
                            </div>
                        </div>
                    </div>
                </div >

                {/* Затемнение фона при открытом мобильном фильтре */}
                {
                    isMobileFilterOpen && (
                        <div
                            className="fixed inset-0 bg-black bg-opacity-50 z-[100000000]"
                            onClick={toggleMobileFilter}
                        ></div>
                    )
                }
            </>
        );
    }

    // Десктопная версия фильтра
    return (
        <div className="bg-white rounded-lg">
            <div className="grid grid-cols-3 gap-3">
                {visibleBrands.map((brand) => (
                    <Link
                        key={brand.id}
                        href={`/product-tag/${categorySlug}?brand=${brand.slug}&page=1`}
                        className={`brand_filter_image flex items-center justify-center p-3 border ${currentBrandFilter === brand.slug
                            ? 'border-[#ff7900] bg-[#fff7ed]'
                            : 'border-[#ddd]'
                            } rounded-[10px] aspect-square`}
                    >
                        {brand.brandThumbnail ? (
                            <div className="flex items-center justify-center h-[50px] w-[50px]">
                                <Image
                                    src={brand.brandThumbnail}
                                    alt={brand.name}
                                    width={50}
                                    height={50}
                                    className="object-contain max-h-[50px]"
                                />
                            </div>
                        ) : (
                            <div className="flex items-center justify-center h-[50px] w-[50px] bg-gray-100 rounded-full">
                                <span className="text-xl font-semibold text-gray-400">{brand.name.charAt(0)}</span>
                            </div>
                        )}
                        <span className="hidden">({brand.productCount})</span>
                    </Link>
                ))}
            </div>

            {/* Разворачиваемый раздел с анимацией для дополнительных брендов */}
            <div className={`overflow-hidden transition-all duration-500 ease-in-out ${isExpanded ? 'max-h-[3000px] opacity-100 mt-3' : 'max-h-0 opacity-0'
                }`}>
                {isExpanded && (
                    <div className="grid grid-cols-3 gap-3 transform transition-all duration-500 ease-in-out">
                        {brands.slice(initialBrandsCount).map((brand, index) => (
                            <Link
                                key={brand.id}
                                href={`/product-tag/${categorySlug}?brand=${brand.slug}&page=1`}
                                className={`brand_filter_image flex items-center justify-center p-3 border ${currentBrandFilter === brand.slug
                                    ? 'border-[#ff7900] bg-[#fff7ed]'
                                    : 'border-[#ddd]'
                                    } rounded-[10px] aspect-square animate-fadeIn`}
                                style={{
                                    animationDelay: `${index * 30}ms`,
                                    opacity: 0,
                                    animation: `fadeIn 300ms ease-in-out ${index * 30}ms forwards`
                                }}
                            >
                                {brand.brandThumbnail ? (
                                    <div className="flex items-center justify-center h-[50px] w-[50px]">
                                        <Image
                                            src={brand.brandThumbnail}
                                            alt={brand.name}
                                            width={50}
                                            height={50}
                                            className="object-contain max-h-[50px]"
                                        />
                                    </div>
                                ) : (
                                    <div className="flex items-center justify-center h-[50px] w-[50px] bg-gray-100 rounded-full">
                                        <span className="text-xl font-semibold text-gray-400">{brand.name.charAt(0)}</span>
                                    </div>
                                )}
                                <span className="hidden">({brand.productCount})</span>
                            </Link>
                        ))}
                    </div>
                )}
            </div>

            {/* Кнопка "Показать больше" только для десктопной версии */}
            {hasMoreBrands && (
                <div className="mt-4 text-center">
                    <button
                        onClick={toggleExpand}
                        className="text-[#ff7900] bg-[#fff] hover:bg-[#fff] hover:underline flex items-center justify-center mx-auto"
                    >
                        <span className="mr-1">{isExpanded ? 'Свернуть' : 'Показать все бренды'}</span>
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className={`transition-transform duration-300 ${isExpanded ? 'rotate-180' : 'rotate-0'}`}
                        >
                            <polyline points="6 9 12 15 18 9"></polyline>
                        </svg>
                    </button>
                </div>
            )}

            <style jsx global>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeIn {
          animation: fadeIn 300ms ease-in-out forwards;
        }
      `}</style>
        </div>
    );
}